/**
 * Due Date Urgency System (Phase 7 Batch 2)
 *
 * Provides color-coded urgency levels based on days until due date.
 * Reusable for billable items, permits, and any future due date tracking.
 *
 * Urgency Levels:
 * - overdue: Past due date (red)
 * - critical: < 7 days (dark orange)
 * - warning: 7-30 days (light orange)
 * - upcoming: 30-60 days (yellow)
 * - on-track: > 60 days (green/default)
 *
 * IMPORTANT: For DATE fields (no time component), we treat them as calendar dates.
 * "2024-12-15" means "Dec 15" everywhere, regardless of timezone.
 * This works correctly across all North America timezones (PST/MST/CST/EST).
 */

export interface DueDateThresholds {
  /** Days before due date to show 'upcoming' urgency (default: 60) */
  upcomingDays: number;
  /** Days before due date to show 'warning' urgency (default: 30) */
  warningDays: number;
  /** Days before due date to show 'critical' urgency (default: 7) */
  criticalDays: number;
}

export type UrgencyLevel = 'overdue' | 'critical' | 'warning' | 'upcoming' | 'on-track';

export interface DueDateUrgency {
  level: UrgencyLevel;
  daysUntilDue: number;
  colorClass: string; // Tailwind background color class
  textColorClass: string; // Tailwind text color class
  label: string; // Human-readable label
}

const DEFAULT_THRESHOLDS: DueDateThresholds = {
  upcomingDays: 60,
  warningDays: 30,
  criticalDays: 7,
};

/**
 * Calculate urgency level and styling for a due date
 *
 * @param dueDate - ISO date string or Date object
 * @param thresholds - Custom thresholds (optional, uses defaults if not provided)
 * @returns DueDateUrgency object with level, days, colors, and label
 *
 * @example
 * const urgency = getDueDateUrgency('2024-12-01');
 * // Returns: { level: 'warning', daysUntilDue: 21, colorClass: 'bg-orange-200', ... }
 */
export function getDueDateUrgency(
  dueDate: string | Date | null | undefined,
  thresholds: Partial<DueDateThresholds> = {}
): DueDateUrgency | null {
  if (!dueDate) {
    return null;
  }

  const mergedThresholds = { ...DEFAULT_THRESHOLDS, ...thresholds };

  // Parse as local calendar date (no timezone shifts)
  // "2024-12-15" means Dec 15 in the local calendar, regardless of timezone
  let due: Date;
  if (typeof dueDate === 'string') {
    const [year, month, day] = dueDate.split('T')[0].split('-').map(Number);
    due = new Date(year, month - 1, day);
  } else {
    // For Date objects, create new date from components to avoid timezone issues
    due = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0); // Normalize to start of day
  due.setHours(0, 0, 0, 0); // Normalize due date to start of day

  const daysUntilDue = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  let level: UrgencyLevel;
  let colorClass: string;
  let textColorClass: string;
  let label: string;

  if (daysUntilDue < 0) {
    level = 'overdue';
    colorClass = 'bg-red-100';
    textColorClass = 'text-red-800';
    label = `${Math.abs(daysUntilDue)} day${Math.abs(daysUntilDue) === 1 ? '' : 's'} overdue`;
  } else if (daysUntilDue <= mergedThresholds.criticalDays) {
    level = 'critical';
    colorClass = 'bg-orange-200';
    textColorClass = 'text-orange-900';
    label = daysUntilDue === 0
      ? 'Due today'
      : `${daysUntilDue} day${daysUntilDue === 1 ? '' : 's'} left`;
  } else if (daysUntilDue <= mergedThresholds.warningDays) {
    level = 'warning';
    colorClass = 'bg-orange-100';
    textColorClass = 'text-orange-800';
    label = `${daysUntilDue} days left`;
  } else if (daysUntilDue <= mergedThresholds.upcomingDays) {
    level = 'upcoming';
    colorClass = 'bg-yellow-100';
    textColorClass = 'text-yellow-800';
    label = `${daysUntilDue} days left`;
  } else {
    level = 'on-track';
    colorClass = 'bg-gray-100';
    textColorClass = 'text-gray-700';
    label = `${daysUntilDue} days left`;
  }

  return {
    level,
    daysUntilDue,
    colorClass,
    textColorClass,
    label,
  };
}

/**
 * Format due date as readable string (e.g., "Dec 1, 2024")
 *
 * Treats input as a calendar date (no timezone shifts).
 * "2024-12-15" displays as "Dec 15, 2024" everywhere (PST, EST, MST, etc.)
 */
export function formatDueDate(dueDate: string | Date | null | undefined): string {
  if (!dueDate) {
    return '';
  }

  // Parse as local calendar date (no timezone shifts)
  let date: Date;
  if (typeof dueDate === 'string') {
    const [year, month, day] = dueDate.split('T')[0].split('-').map(Number);
    date = new Date(year, month - 1, day);
  } else {
    // For Date objects, create new date from components
    date = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}
